import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bike, Handshake, MessageCircle, Wallet, Check, ArrowRight, LayoutDashboard, Users, FileText,
  Bell, CalendarClock, CreditCard, Headphones, CircleX, MousePointerClick, RefreshCw, ShieldCheck,
  ChartColumn, UsersRound,
} from 'lucide-react';

// Site público (automais.pro). Só visual: rotas /login e /cadastro continuam iguais.
// Os "prints" do sistema são mockups desenhados em código com dados fictícios —
// screenshot real exporia dados de clientes/leads.

const NAVY = '#0A1628';
const CIANO = '#00C8FF';
const AZUL = '#1A6DFF'; // azul do sistema (usado nos mockups)
const VERDE = '#22C55E';
const TEXTO = '#0F172A';
const SUAVE = '#64748B';
const FUNDO = '#F6F8FB';

// Hover, media queries e fonte não cabem em style inline: CSS escopado com prefixo lp-.
const CSS = `
body { margin: 0; } /* sem index.css: margem padrão de 8px do navegador */
html { scroll-behavior: smooth; }
.lp { font-family: 'Inter', system-ui, -apple-system, sans-serif; color: ${TEXTO}; background: #fff; -webkit-font-smoothing: antialiased; }
.lp * { box-sizing: border-box; }
.lp h1, .lp h2, .lp h3 { margin: 0; letter-spacing: -0.02em; }
.lp p { margin: 0; }
.lp a { color: inherit; }
.lp-wrap { max-width: 1180px; margin: 0 auto; padding: 0 32px; }
.lp-nav { position: sticky; top: 0; z-index: 100; background: rgba(10,22,40,0.92); backdrop-filter: blur(10px); border-bottom: 1px solid rgba(255,255,255,0.06); }
.lp-nav-in { height: 70px; display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 24px; }
.lp-links { display: flex; gap: 32px; }
.lp .lp-link { color: #CBD5E1; text-decoration: none; font-size: 14px; font-weight: 500; transition: color .2s; }
.lp .lp-link:hover { color: ${CIANO}; }
.lp-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; border: none; cursor: pointer; font-family: inherit; font-weight: 600; text-decoration: none; border-radius: 12px; transition: transform .2s, box-shadow .2s, background .2s, color .2s; }
.lp-btn:hover { transform: translateY(-2px); }
.lp-btn-ciano { background: ${CIANO}; color: ${NAVY} !important; box-shadow: 0 8px 24px rgba(0,200,255,0.28); }
.lp-btn-ciano:hover { background: #3DD6FF; box-shadow: 0 14px 32px rgba(0,200,255,0.38); }
.lp-btn-ghost { background: rgba(255,255,255,0.06); color: #fff !important; border: 1px solid rgba(255,255,255,0.16); }
.lp-btn-ghost:hover { background: rgba(255,255,255,0.12); border-color: rgba(0,200,255,0.5); }
.lp-btn-navy { background: ${NAVY}; color: #fff !important; }
.lp-btn-navy:hover { background: #13284A; box-shadow: 0 12px 28px rgba(10,22,40,0.25); }
.lp-hero { background: radial-gradient(900px 520px at 88% 0%, rgba(0,200,255,0.20), transparent 60%), radial-gradient(700px 400px at 0% 100%, rgba(26,109,255,0.18), transparent 60%), ${NAVY}; color: #fff; padding: 92px 0 104px; overflow: hidden; }
.lp-hero-grid { display: grid; grid-template-columns: 1fr 1.12fr; gap: 56px; align-items: center; }
.lp-sec { padding: 104px 0; }
.lp-titulo { font-size: 40px; font-weight: 800; line-height: 1.15; }
.lp-sub { font-size: 17px; color: ${SUAVE}; line-height: 1.7; }
.lp-eyebrow { font-size: 13px; font-weight: 700; color: #0891B2; text-transform: uppercase; letter-spacing: .08em; margin-bottom: 12px; }
.lp-card { background: #fff; border-radius: 16px; box-shadow: 0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06); transition: transform .25s ease, box-shadow .25s ease; }
.lp-card:hover { transform: translateY(-6px); box-shadow: 0 2px 4px rgba(15,23,42,0.05), 0 22px 44px rgba(15,23,42,0.12); }
.lp-feat { display: grid; grid-template-columns: 1.1fr 1fr; gap: 72px; align-items: center; }
.lp-feat + .lp-feat { margin-top: 112px; }
.lp-feat.inv .lp-feat-img { order: 2; }
.lp-feat-img { transition: transform .3s ease; }
.lp-feat-img:hover { transform: translateY(-6px); }
.lp-nums { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
.lp-motivos { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
.lp-planos { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; align-items: stretch; }
.lp-prova { display: flex; gap: 20px 64px; justify-content: center; align-items: center; flex-wrap: wrap; }
.lp-badges { display: flex; gap: 22px; flex-wrap: wrap; }
.lp-foot { display: grid; grid-template-columns: 1.4fr 1fr 1fr 1fr; gap: 40px; }
.lp-foot a { color: #94A3B8; text-decoration: none; font-size: 14px; transition: color .2s; display: block; margin-bottom: 12px; }
.lp-foot a:hover { color: ${CIANO}; }
.lp-frame { background: #fff; border-radius: 14px; overflow: hidden; box-shadow: 0 30px 80px rgba(2,8,23,0.5), 0 0 0 1px rgba(255,255,255,0.06); }
.lp-shot { border-radius: 16px; overflow: hidden; background: #fff; box-shadow: 0 2px 6px rgba(15,23,42,0.06), 0 24px 60px rgba(15,23,42,0.12); }
@media (max-width: 960px) {
  .lp-nav-in { grid-template-columns: auto 1fr; }
  .lp-links { display: none; }
  .lp-hero-grid, .lp-feat { grid-template-columns: 1fr; gap: 40px; }
  .lp-feat.inv .lp-feat-img { order: 0; }
  .lp-nums, .lp-motivos { grid-template-columns: repeat(2, 1fr); }
  .lp-planos { grid-template-columns: 1fr; max-width: 420px; margin: 0 auto; }
  .lp-foot { grid-template-columns: 1fr 1fr; }
  .lp-titulo { font-size: 32px; }
  .lp-h1 { font-size: 42px !important; }
  .lp-sec { padding: 72px 0; }
  .lp-feat + .lp-feat { margin-top: 72px; }
}
@media (max-width: 640px) {
  .lp-wrap { padding: 0 20px; }
  .lp-motivos { grid-template-columns: 1fr; }
  .lp-h1 { font-size: 34px !important; }
  .lp-hero { padding: 56px 0 64px; }
  .lp-esconde-mobile { display: none !important; }
  .lp-badges { gap: 12px 18px; }
}
`;

// ---------- Mockups do sistema (dados fictícios) ----------
const brl = v => 'R$ ' + v.toLocaleString('pt-BR');

function BarraJanela({ url, escuro }) {
  return (
    <div style={{ height: 34, display: 'flex', alignItems: 'center', gap: 6, padding: '0 14px', background: escuro ? '#0F1D33' : '#F1F4F9', borderBottom: `1px solid ${escuro ? 'rgba(255,255,255,0.06)' : '#E5EAF2'}` }}>
      {['#FF5F57', '#FEBC2E', '#28C840'].map(c => <span key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />)}
      <span style={{ marginLeft: 14, flex: 1, maxWidth: 260, height: 20, borderRadius: 6, background: escuro ? 'rgba(255,255,255,0.06)' : '#fff', fontSize: 10, color: escuro ? '#64748B' : '#94A3B8', display: 'flex', alignItems: 'center', paddingLeft: 10 }}>{url}</span>
    </div>
  );
}

function Kpi({ rotulo, valor, cor, extra }) {
  return (
    <div style={{ background: '#16233A', borderRadius: 10, padding: '12px 14px', minWidth: 0 }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: '#8193AE', letterSpacing: '.06em', textTransform: 'uppercase' }}>{rotulo}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', marginTop: 6 }}>{valor}</div>
      {extra && <div style={{ fontSize: 9, color: cor, marginTop: 3, fontWeight: 600 }}>{extra}</div>}
    </div>
  );
}

function MockDashboard() {
  const barras = [42, 55, 48, 66, 58, 74, 69, 88, 80, 96];
  const menu = [[LayoutDashboard, 'Dashboard'], [Bike, 'Estoque'], [Users, 'Clientes'], [Handshake, 'Vendas'], [MessageCircle, 'CRM'], [Wallet, 'Financeiro']];
  return (
    <div className="lp-frame">
      <BarraJanela url="automais.pro/dashboard" escuro />
      <div style={{ display: 'flex', background: '#EEF2F7', minHeight: 360 }}>
        <div className="lp-esconde-mobile" style={{ width: 132, background: NAVY, padding: '14px 10px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 18, paddingLeft: 4 }}>
            <span style={{ width: 22, height: 22, borderRadius: 6, background: '#2D7D4F', color: '#fff', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A+</span>
            <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>AutoMais</span>
          </div>
          {menu.map(([Ic, t], i) => (
            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 8px', borderRadius: 7, marginBottom: 2, background: i === 0 ? AZUL : 'transparent', color: i === 0 ? '#fff' : '#94A3B8', fontSize: 10, fontWeight: 600 }}>
              <Ic size={12} />{t}
            </div>
          ))}
        </div>
        <div style={{ flex: 1, padding: 16, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: TEXTO, marginBottom: 12 }}>Dashboard <span style={{ fontWeight: 500, color: SUAVE, fontSize: 10 }}>· outubro</span></div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
            <Kpi rotulo="Motos em estoque" valor="38" extra="R$ 612 mil em estoque" cor="#FBBF24" />
            <Kpi rotulo="Vendas no mês" valor="17" extra="▲ 21% vs. setembro" cor="#4ADE80" />
            <Kpi rotulo="Lucro do mês" valor="R$ 48,2 mil" extra="margem média 14%" cor="#60A5FA" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 8 }}>
            <div style={{ background: '#fff', borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: TEXTO, marginBottom: 10 }}>Faturamento</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 110 }}>
                {barras.map((h, i) => <div key={i} style={{ flex: 1, height: h + '%', borderRadius: '4px 4px 0 0', background: i === barras.length - 1 ? AZUL : '#C7D7F5' }} />)}
              </div>
            </div>
            <div style={{ background: '#fff', borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: TEXTO, marginBottom: 8 }}>Meta do mês</div>
              {[['Elétricas', 70], ['Até 190cc', 85], ['Acima de 200cc', 45]].map(([t, p]) => (
                <div key={t} style={{ marginBottom: 9 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: SUAVE, marginBottom: 4 }}><span>{t}</span><span style={{ fontWeight: 700, color: TEXTO }}>{p}%</span></div>
                  <div style={{ height: 6, borderRadius: 3, background: '#EEF2F7' }}><div style={{ width: p + '%', height: '100%', borderRadius: 3, background: p >= 80 ? VERDE : AZUL }} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MockEstoque() {
  const motos = [
    ['Honda CG 160 Fan', '2024', 18900, 15, '8d'],
    ['Yamaha Fazer 250', '2022', 17500, 20, '21d'],
    ['Honda NXR 160 Bros', '2023', 19990, 18, '12d'],
    ['Honda Biz 125', '2021', 11900, 22, '43d'],
  ];
  return (
    <div className="lp-shot">
      <BarraJanela url="automais.pro/estoque" />
      <div style={{ padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>Estoque <span style={{ color: SUAVE, fontWeight: 500, fontSize: 11 }}>(38 veículos)</span></div>
          <span style={{ background: NAVY, color: '#fff', fontSize: 10, fontWeight: 700, padding: '6px 10px', borderRadius: 7 }}>+ Novo veículo</span>
        </div>
        {motos.map(([m, a, v, mg, d]) => (
          <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: '1px solid #EEF2F7' }}>
            <div style={{ width: 44, height: 34, borderRadius: 8, background: 'linear-gradient(135deg,#DCE6F7,#F1F5FB)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7C93B8', flexShrink: 0 }}><Bike size={18} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m}</div>
              <div style={{ fontSize: 10, color: SUAVE }}>{a} · margem {mg}%</div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#15803D' }}>{brl(v)}</div>
            <span style={{ fontSize: 9, fontWeight: 700, padding: '3px 7px', borderRadius: 10, background: parseInt(d) > 30 ? '#FDECEA' : '#EAF5EE', color: parseInt(d) > 30 ? '#C0392B' : '#2D7D4F' }}>{d}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockVenda() {
  return (
    <div className="lp-shot" style={{ padding: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 10, color: SUAVE, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>Venda #1042</div>
          <div style={{ fontSize: 15, fontWeight: 800, marginTop: 4 }}>Honda CG 160 Fan 2024</div>
          <div style={{ fontSize: 11, color: SUAVE, marginTop: 2 }}>Cliente: Rafael Souza · Vendedor: Ana</div>
        </div>
        <span style={{ background: '#EAF5EE', color: '#2D7D4F', fontSize: 10, fontWeight: 700, padding: '4px 9px', borderRadius: 20 }}>Entregue</span>
      </div>
      {[['Entrada (PIX)', 5000, true], ['Moto na troca', 6500, true], ['Financiamento', 7400, false]].map(([t, v, pago]) => (
        <div key={t} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: FUNDO, borderRadius: 9, marginBottom: 6, fontSize: 12 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: pago ? VERDE : '#F59E0B' }} />{t}</span>
          <strong>{brl(v)}</strong>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 2px 0', fontSize: 13, fontWeight: 800 }}><span>Total</span><span>{brl(18900)}</span></div>
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        {['Contrato PDF', 'Garantia PDF'].map(t => (
          <span key={t} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11, fontWeight: 700, padding: '9px 0', borderRadius: 8, background: '#EEF4FF', color: AZUL }}><FileText size={13} />{t}</span>
        ))}
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, padding: '9px 0', borderRadius: 8, background: '#FFF7E6', color: '#B45309' }}>Comissão {brl(380)}</span>
      </div>
    </div>
  );
}

function MockCRM() {
  const colunas = [
    ['Novo', '#1A3A5C', [['Bruno L.', 'Instagram', 'Hoje']]],
    ['Em contato', '#1A6DFF', [['Carla M.', 'WhatsApp', 'Amanhã'], ['Diego R.', 'Site', 'Hoje']]],
    ['Simulando', '#7C3AED', [['Fernanda P.', 'Indicação', 'Sex']]],
  ];
  return (
    <div className="lp-shot" style={{ padding: 18, background: '#EEF2F7' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', borderRadius: 10, padding: '9px 12px', marginBottom: 12, fontSize: 11, color: '#B45309', fontWeight: 600 }}>
        <Bell size={13} /> 2 follow-ups para hoje
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        {colunas.map(([t, cor, leads]) => (
          <div key={t} style={{ background: '#F8FAFC', borderRadius: 10, padding: 10, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, marginBottom: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: cor }} />{t}
              <span style={{ marginLeft: 'auto', fontSize: 10, color: SUAVE }}>{leads.length}</span>
            </div>
            {leads.map(([n, o, f]) => (
              <div key={n} style={{ background: '#fff', borderRadius: 8, padding: '9px 10px', marginBottom: 8, boxShadow: '0 1px 3px rgba(15,23,42,0.08)' }}>
                <div style={{ fontSize: 11, fontWeight: 700 }}>{n}</div>
                <div style={{ fontSize: 9, color: SUAVE, margin: '2px 0 6px' }}>{o}</div>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 9, fontWeight: 600, color: f === 'Hoje' ? '#B45309' : SUAVE }}><CalendarClock size={10} />{f}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function MockFinanceiro() {
  const linhas = [['Venda CG 160 — parcela 2/3', 1800, 'receber'], ['Aluguel da loja', 3200, 'pagar'], ['Compra Fazer 250', 14000, 'pagar'], ['Venda Bros 160', 19990, 'receber']];
  return (
    <div className="lp-shot">
      <BarraJanela url="automais.pro/financeiro" />
      <div style={{ padding: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
          {[['A receber (30d)', 'R$ 64,3 mil', '#15803D'], ['A pagar (30d)', 'R$ 41,7 mil', '#C0392B'], ['Resultado do mês', 'R$ 22,6 mil', AZUL]].map(([t, v, c]) => (
            <div key={t} style={{ background: FUNDO, borderRadius: 10, padding: '10px 12px', minWidth: 0 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: SUAVE, textTransform: 'uppercase', letterSpacing: '.05em' }}>{t}</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: c, marginTop: 4 }}>{v}</div>
            </div>
          ))}
        </div>
        {linhas.map(([t, v, tipo]) => (
          <div key={t} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderTop: '1px solid #EEF2F7', fontSize: 11 }}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t}</span>
            <strong style={{ color: tipo === 'receber' ? '#15803D' : '#C0392B', flexShrink: 0, marginLeft: 10 }}>{tipo === 'receber' ? '+' : '−'}{brl(v)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Conteúdo ----------
const FUNCIONALIDADES = [
  { icone: Bike, titulo: 'Controle de Estoque', texto: 'Cadastre motos com fotos, gerencie custo, margem e dias parados.', itens: ['Fotos e documentos de cada moto', 'Custo com despesas e margem na hora', 'Destaque para moto parada há mais de 30 dias'], mock: <MockEstoque /> },
  { icone: Handshake, titulo: 'Gestão de Vendas', texto: 'Contratos, pagamentos parciais e comissões. Histórico completo.', itens: ['Contrato e garantia em PDF', 'Entrada, troca e financiamento na mesma venda', 'Comissão do vendedor calculada'], mock: <MockVenda /> },
  { icone: Wallet, titulo: 'Financeiro Completo', texto: 'A Pagar, A Receber, extrato bancário e dashboard com resultado do mês.', itens: ['Contas a pagar e a receber', 'Importação de extrato bancário (OFX)', 'Resultado do mês sempre atualizado'], mock: <MockFinanceiro /> },
  { icone: MessageCircle, titulo: 'CRM de Leads', texto: 'Funil de vendas, follow-up e alertas para não perder nenhum cliente.', itens: ['Funil em colunas, do primeiro contato à venda', 'Lembrete de follow-up no dia certo', 'Cada etapa da negociação à vista'], mock: <MockCRM /> },
];

// Números reais (média de motos vendidas/mês pelas revendas no sistema, abr–set/2026 ≈ 28)
const NUMEROS = [
  ['30+', 'motos vendidas por mês no sistema'],
  ['3', 'revendas usando no dia a dia'],
  ['100%', 'online, do computador ao celular'],
  ['24h', 'para responder o suporte'],
];

const MOTIVOS = [
  [MousePointerClick, 'Fácil de usar', 'Pensado para a rotina da revenda. A equipe aprende em pouco tempo, sem treinamento longo.'],
  [Headphones, 'Suporte rápido', 'Fale direto com quem conhece o sistema. Resposta em até 24h.'],
  [RefreshCw, 'Atualizações', 'Melhorias entram no ar sem custo extra e sem você instalar nada.'],
  [ShieldCheck, 'Nuvem segura', 'Dados na nuvem com backup diário. Acesse de qualquer lugar.'],
  [ChartColumn, 'Relatórios', 'Lucro por venda, faturamento, comissões e metas em tempo real.'],
  [UsersRound, 'Multi-usuário', 'Cada vendedor com seu acesso, e você define o que cada um pode ver.'],
];

// Preços = o que o Asaas cobra (services/asaas.js PRECOS_PLANOS)
const PLANOS = [
  { nome: 'Starter', preco: 'R$99', periodo: '/mês', destaque: false, items: ['Até 20 motos no estoque', 'Cadastro de clientes', 'Gestão de vendas', 'Contrato e garantia em PDF', 'Relatório básico', '1 usuário'] },
  { nome: 'Pro', preco: 'R$150', periodo: '/mês', destaque: true, items: ['Tudo do Starter', 'Site público integrado', 'Motos ilimitadas', 'CRM de leads', 'Financeiro completo', 'Fornecedores', 'Relatórios avançados', 'Até 5 usuários'] },
  { nome: 'Enterprise', preco: 'Sob consulta', periodo: '', destaque: false, items: ['Tudo do Pro', 'Fluxo de caixa', 'Importação OFX', 'Integração Asaas', 'Usuários ilimitados', 'Suporte prioritário'] },
];

const CLIENTES = ['Autop Motos', 'Sales Motocar', 'Dual Motors'];

// Logo em /public/images; até o arquivo existir, cai na marca "A+" do sistema.
function Logo({ circular = false, altura = 38 }) {
  const [falhou, setFalhou] = useState(false);
  if (!falhou) {
    return <img src={circular ? '/images/logo-circular.png' : '/images/logo-horizontal.png'} alt="AutoMais" onError={() => setFalhou(true)} style={{ height: circular ? 64 : altura, display: 'block' }} />;
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <span style={{ width: 34, height: 34, borderRadius: circular ? '50%' : 9, background: CIANO, color: NAVY, fontWeight: 800, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A+</span>
      <span style={{ fontSize: 20, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>Auto<span style={{ color: CIANO }}>Mais</span></span>
    </span>
  );
}

function Cabecalho({ eyebrow, titulo, sub, escuro }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: 64 }}>
      <div className="lp-eyebrow" style={escuro ? { color: CIANO } : undefined}>{eyebrow}</div>
      <h2 className="lp-titulo" style={{ marginBottom: 14 }}>{titulo}</h2>
      {sub && <p className="lp-sub" style={{ maxWidth: 560, margin: '0 auto', color: escuro ? '#A5B4CB' : undefined }}>{sub}</p>}
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const cadastro = () => navigate('/cadastro');
  const entrar = () => navigate('/login');

  return (
    <div className="lp">
      <style>{CSS}</style>

      {/* 1. NAVBAR */}
      <nav className="lp-nav">
        <div className="lp-wrap lp-nav-in">
          <a href="#topo" style={{ textDecoration: 'none', justifySelf: 'start' }}><Logo /></a>
          <div className="lp-links">
            <a href="#funcionalidades" className="lp-link">Funcionalidades</a>
            <a href="#por-que" className="lp-link">Por que AutoMais</a>
            <a href="#precos" className="lp-link">Preços</a>
          </div>
          <div style={{ display: 'flex', gap: 10, justifySelf: 'end' }}>
            <button onClick={entrar} className="lp-btn lp-btn-ghost" style={{ padding: '10px 18px', fontSize: 14 }}>Entrar</button>
            <button onClick={cadastro} className="lp-btn lp-btn-ciano lp-esconde-mobile" style={{ padding: '10px 18px', fontSize: 14 }}>Começar grátis</button>
          </div>
        </div>
      </nav>

      {/* 2. HERO */}
      <section id="topo" className="lp-hero">
        <div className="lp-wrap lp-hero-grid">
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(0,200,255,0.10)', color: CIANO, border: '1px solid rgba(0,200,255,0.28)', padding: '6px 14px', borderRadius: 30, fontSize: 13, fontWeight: 600, marginBottom: 26 }}>
              <Bike size={15} /> Gestão completa para revendas de motos
            </div>
            <h1 className="lp-h1" style={{ fontSize: 56, fontWeight: 800, lineHeight: 1.06, marginBottom: 22 }}>
              O sistema que sua <span style={{ color: CIANO }}>revenda</span> precisava
            </h1>
            <p style={{ fontSize: 18, color: '#A5B4CB', lineHeight: 1.7, marginBottom: 36, maxWidth: 520 }}>
              Estoque, vendas, clientes, financeiro e site integrado. Tudo em um só lugar, 100% online.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 36 }}>
              <button onClick={cadastro} className="lp-btn lp-btn-ciano" style={{ padding: '16px 28px', fontSize: 16 }}>Começar grátis <ArrowRight size={18} /></button>
              <a href="#funcionalidades" className="lp-btn lp-btn-ghost" style={{ padding: '16px 28px', fontSize: 16 }}>Ver funcionalidades</a>
            </div>
            <div className="lp-badges">
              {[[CreditCard, 'Sem cartão de crédito'], [Headphones, 'Suporte incluso'], [CircleX, 'Cancele quando quiser']].map(([Ic, t]) => (
                <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#CBD5E1' }}>
                  <Ic size={17} color={CIANO} />{t}
                </span>
              ))}
            </div>
          </div>
          <MockDashboard />
        </div>
      </section>

      {/* 3. CLIENTES */}
      <section style={{ padding: '44px 0', borderBottom: '1px solid #EEF2F7' }}>
        <div className="lp-wrap">
          <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 600, color: SUAVE, letterSpacing: '.06em', marginBottom: 22 }}>REVENDAS QUE JÁ USAM O AUTOMAIS</div>
          <div className="lp-prova">
            {CLIENTES.map(n => (
              <span key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontSize: 21, fontWeight: 800, color: '#94A3B8', letterSpacing: '-0.01em' }}>
                <Bike size={22} strokeWidth={2.2} />{n}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 4. FUNCIONALIDADES */}
      <section id="funcionalidades" className="lp-sec" style={{ background: FUNDO }}>
        <div className="lp-wrap">
          <Cabecalho eyebrow="Funcionalidades" titulo="Tudo que sua revenda precisa" sub="Do estoque ao financeiro, tudo integrado em um único sistema." />
          {FUNCIONALIDADES.map(({ icone: Icone, titulo, texto, itens, mock }, i) => (
            <div key={titulo} className={'lp-feat' + (i % 2 ? ' inv' : '')}>
              <div className="lp-feat-img">{mock}</div>
              <div>
                <div style={{ width: 50, height: 50, borderRadius: 14, background: 'rgba(0,200,255,0.12)', color: '#0891B2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}><Icone size={24} /></div>
                <h3 style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.2, marginBottom: 14 }}>{titulo}</h3>
                <p className="lp-sub" style={{ marginBottom: 24 }}>{texto}</p>
                {itens.map(it => (
                  <div key={it} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, fontSize: 15, color: '#334155' }}>
                    <span style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(0,200,255,0.14)', color: '#0891B2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Check size={13} strokeWidth={3} /></span>
                    {it}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 5. NÚMEROS */}
      <section style={{ padding: '88px 0', background: NAVY, color: '#fff' }}>
        <div className="lp-wrap lp-nums">
          {NUMEROS.map(([n, t]) => (
            <div key={t} style={{ textAlign: 'center', padding: '8px 12px' }}>
              <div style={{ fontSize: 52, fontWeight: 800, letterSpacing: '-0.03em', color: CIANO, lineHeight: 1 }}>{n}</div>
              <div style={{ fontSize: 15, color: '#A5B4CB', marginTop: 12 }}>{t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 6. POR QUE AUTOMAIS */}
      <section id="por-que" className="lp-sec" style={{ background: '#fff' }}>
        <div className="lp-wrap">
          <Cabecalho eyebrow="Por que AutoMais" titulo="Feito para revendas de motos" sub="Não é um sistema genérico adaptado: cada tela foi pensada para o dia a dia da revenda." />
          <div className="lp-motivos">
            {MOTIVOS.map(([Ic, t, d]) => (
              <div key={t} className="lp-card" style={{ padding: '32px 28px' }}>
                <div style={{ width: 50, height: 50, borderRadius: 14, background: NAVY, color: CIANO, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}><Ic size={23} /></div>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{t}</div>
                <div style={{ fontSize: 14.5, color: SUAVE, lineHeight: 1.65 }}>{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. PREÇOS */}
      <section id="precos" className="lp-sec" style={{ background: FUNDO }}>
        <div className="lp-wrap" style={{ maxWidth: 1080 }}>
          <Cabecalho eyebrow="Preços" titulo="Planos e preços" sub="Sem fidelidade. Cancele quando quiser." />
          <div className="lp-planos">
            {PLANOS.map(p => (
              <div key={p.nome} className="lp-card" style={{ padding: '36px 30px', position: 'relative', display: 'flex', flexDirection: 'column', border: p.destaque ? `2px solid ${CIANO}` : '2px solid transparent', boxShadow: p.destaque ? '0 24px 60px rgba(0,200,255,0.22)' : undefined }}>
                {p.destaque && <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: CIANO, color: NAVY, padding: '5px 14px', borderRadius: 20, fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap', letterSpacing: '.05em' }}>MAIS POPULAR</div>}
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>{p.nome}</div>
                <div style={{ fontSize: p.periodo ? 44 : 34, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, minHeight: 44, display: 'flex', alignItems: 'flex-end' }}>
                  {p.preco}{p.periodo && <span style={{ fontSize: 16, fontWeight: 500, color: SUAVE, marginLeft: 2, marginBottom: 4 }}>{p.periodo}</span>}
                </div>
                <div style={{ margin: '26px 0', borderTop: '1px solid #EEF2F7' }} />
                <div style={{ flex: 1 }}>
                  {p.items.map(item => (
                    <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, fontSize: 14, color: '#475569' }}>
                      <Check size={16} strokeWidth={3} color="#0891B2" style={{ flexShrink: 0 }} />{item}
                    </div>
                  ))}
                </div>
                <button onClick={cadastro} className={'lp-btn ' + (p.destaque ? 'lp-btn-ciano' : 'lp-btn-navy')} style={{ width: '100%', marginTop: 24, padding: 14, fontSize: 15 }}>
                  Começar agora
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. CTA FINAL */}
      <section style={{ padding: '96px 0', background: `linear-gradient(120deg, ${CIANO} 0%, ${AZUL} 100%)`, textAlign: 'center', color: '#fff' }}>
        <div className="lp-wrap">
          <h2 className="lp-titulo" style={{ fontSize: 44, marginBottom: 16 }}>Pronto para organizar sua revenda?</h2>
          <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.9)', marginBottom: 36 }}>Crie sua conta agora. Sem cartão de crédito e sem fidelidade.</p>
          <button onClick={cadastro} className="lp-btn" style={{ padding: '20px 48px', fontSize: 18, fontWeight: 800, background: '#fff', color: NAVY, borderRadius: 14, boxShadow: '0 14px 36px rgba(10,22,40,0.25)' }}>
            Começar grátis <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* 9. FOOTER */}
      <footer style={{ padding: '64px 0 32px', background: NAVY, color: '#fff' }}>
        <div className="lp-wrap">
          <div className="lp-foot">
            <div>
              <Logo circular />
              <p style={{ color: '#94A3B8', fontSize: 14, lineHeight: 1.7, marginTop: 16, maxWidth: 280 }}>Sistema de gestão para revendas de motos.</p>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.06em', marginBottom: 16 }}>PRODUTO</div>
              <a href="#funcionalidades">Funcionalidades</a>
              <a href="#por-que">Por que AutoMais</a>
              <a href="#precos">Preços</a>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.06em', marginBottom: 16 }}>CONTA</div>
              <a href="/login" onClick={e => { e.preventDefault(); entrar(); }}>Entrar</a>
              <a href="/cadastro" onClick={e => { e.preventDefault(); cadastro(); }}>Criar conta</a>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.06em', marginBottom: 16 }}>RECURSOS</div>
              <a href="#funcionalidades">Estoque e vendas</a>
              <a href="#funcionalidades">Financeiro</a>
              <a href="#funcionalidades">CRM</a>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 40, paddingTop: 24, color: '#64748B', fontSize: 13 }}>
            © 2026 AutoMais · Sistema de Gestão para Revendas de Motos · automais.pro
          </div>
        </div>
      </footer>
    </div>
  );
}
